import {
  ArrowLeftOutlined,
  PushpinFilled,
  PushpinOutlined,
  ShareAltOutlined,
} from '@ant-design/icons'
import {
  ThumbDown,
  ThumbDownOutlined,
  ThumbUp,
  ThumbUpOutlined,
} from '@mui/icons-material'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { darkBlueFontColor } from 'assets/styles/variables'
import {
  ISSUE_TRACK_TOOLTIP,
  ISSUE_UNTRACK_TOOLTIP,
  TREND_DETECTION_PRIMARY_BLUE,
} from 'features/trend-detection/constants'

export interface InsightDetailSubHeaderProps {
  onBack: () => void
  backLabel?: string
  upvoteActive?: boolean
  downvoteActive?: boolean
  onUpvote?: () => void
  onDownvote?: () => void
  feedbackDisabled?: boolean
  isTracked?: boolean
  onTrackingChange?: (nextTracked: boolean) => void
  trackingLoading?: boolean
  trackingDisabled?: boolean
  onShare?: () => void
}

export const InsightDetailSubHeader: React.FC<InsightDetailSubHeaderProps> = ({
  onBack,
  backLabel = 'Back to insights',
  upvoteActive = false,
  downvoteActive = false,
  onUpvote,
  onDownvote,
  feedbackDisabled = false,
  isTracked = false,
  onTrackingChange,
  trackingLoading = false,
  trackingDisabled = false,
  onShare,
}) => {
  const [copied, setCopied] = useState(false)

  const handleShare = (): void => {
    void navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      onShare?.()
    })
  }

  return (
    <SubHeaderBar>
      <BackButton onClick={onBack}>
        <ArrowLeftOutlined />
        {backLabel}
      </BackButton>
      <RightActions>
        <Tooltip
          content={!feedbackDisabled ? 'This insight is helpful' : undefined}
        >
          <span>
            <IconActionButton
              onClick={!feedbackDisabled ? onUpvote : undefined}
              disabled={feedbackDisabled}
              $active={upvoteActive}
              aria-label="Upvote"
            >
              {upvoteActive ? (
                <ThumbUp sx={{ fontSize: 16 }} />
              ) : (
                <ThumbUpOutlined sx={{ fontSize: 16 }} />
              )}
            </IconActionButton>
          </span>
        </Tooltip>
        <Tooltip
          content={
            !feedbackDisabled ? 'This insight needs improvement' : undefined
          }
        >
          <span>
            <IconActionButton
              onClick={!feedbackDisabled ? onDownvote : undefined}
              disabled={feedbackDisabled}
              $active={downvoteActive}
              aria-label="Downvote"
            >
              {downvoteActive ? (
                <ThumbDown sx={{ fontSize: 16 }} />
              ) : (
                <ThumbDownOutlined sx={{ fontSize: 16 }} />
              )}
            </IconActionButton>
          </span>
        </Tooltip>
        <Tooltip content={copied ? 'Copied!' : 'Copy link'}>
          <span>
            <IconActionButton onClick={handleShare} aria-label="Copy link">
              <ShareAltOutlined />
            </IconActionButton>
          </span>
        </Tooltip>
        <Tooltip
          content={
            trackingDisabled
              ? 'Tracking coming soon'
              : isTracked
                ? ISSUE_UNTRACK_TOOLTIP
                : ISSUE_TRACK_TOOLTIP
          }
        >
          <span>
            <TrackingToggleButton
              variant="secondary"
              loading={trackingLoading}
              disabled={trackingDisabled}
              $tracked={isTracked}
              onClick={() => onTrackingChange?.(!isTracked)}
            >
              {isTracked ? (
                <TrackingPinIconTracked>
                  <PushpinFilled />
                </TrackingPinIconTracked>
              ) : (
                <TrackingPinIcon>
                  <PushpinOutlined />
                </TrackingPinIcon>
              )}
              <TrackingStatusText>
                {isTracked ? 'Tracking' : 'Track'}
              </TrackingStatusText>
            </TrackingToggleButton>
          </span>
        </Tooltip>
      </RightActions>
    </SubHeaderBar>
  )
}

const SubHeaderBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px;
  background: #fcfafa;
  border-bottom: 1px solid #d8d8d8;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  color: ${darkBlueFontColor};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${TREND_DETECTION_PRIMARY_BLUE};
    outline-offset: 2px;
  }
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const IconActionButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${(p) =>
    p.$active ? TREND_DETECTION_PRIMARY_BLUE : 'rgba(31, 52, 84, 0.55)'};
  transition:
    background 0.15s,
    color 0.15s;

  &:hover:not(:disabled) {
    background: rgba(31, 52, 84, 0.06);
    color: ${darkBlueFontColor};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${TREND_DETECTION_PRIMARY_BLUE};
    outline-offset: 2px;
  }
`

const TrackingToggleButton = styled(Button)<{ $tracked: boolean }>`
  height: 28px;
  padding-inline: 12px;
  border-radius: 999px;
  border-color: ${(p) =>
    p.$tracked ? 'rgba(79, 114, 164, 0.28)' : 'rgba(31, 52, 84, 0.16)'};
  background: ${(p) =>
    p.$tracked ? 'rgba(79, 114, 164, 0.08)' : 'rgba(255, 255, 255, 0.92)'};

  &&:hover:not(:disabled):not([aria-disabled='true']),
  &&:focus-visible {
    border-color: rgba(79, 114, 164, 0.4);
    color: ${darkBlueFontColor};
    background: ${(p) =>
      p.$tracked ? 'rgba(79, 114, 164, 0.08)' : 'rgba(255, 255, 255, 0.92)'};
  }
`

const TrackingPinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.72;
`

const TrackingPinIconTracked = styled(TrackingPinIcon)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
`

const TrackingStatusText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${darkBlueFontColor};
  opacity: 0.92;
`
